import * as _ from 'lodash';
import * as Autosuggest from 'react-autosuggest';
import Dropzone from 'react-dropzone';
import * as React from 'react';
import * as Validator from 'validator';
import * as Model from '../../api/model';
import * as Request from '../../api/request';
import {
    WithStyles, Theme, Dialog, DialogTitle, DialogContent, DialogContentText, FormControl, Select, FormHelperText, CircularProgress, MenuItem,
    InputLabel, InputAdornment, TextField, Paper, Chip, Button, Grid, Typography, DialogActions, Snackbar, IconButton, Portal, Tooltip, FormControlLabel, Checkbox
} from '@material-ui/core';
import withStyles, { CSSProperties } from '@material-ui/core/styles/withStyles';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import MapComponent from './MapComponent';
import { API } from '../../api';
import { WineryBuilder } from '../../api/request/builder';

//#region UI configuration
const strings = {
    title: 'Winery Form',
    message: 'Provide winery data',
    country: 'Country',
    region: 'Region',
    subregion: 'Subregion',
    appellation: 'Appellation',
    domain: 'Domain',
    classification: 'Classification',
    producer: 'Producer',
    address: 'Address',
    city: 'City',
    street: 'Street',
    postalCode: 'Postal code',
    post: 'Post',
    phone: 'Phone',
    email: 'E-mail',
    website: 'Website',
    plantation: 'Plantation',
    redVarieties: 'Red varieties',
    whiteVarieties: 'White varieties',
    labels: 'Labels',
    clearLocation: 'Clear location',
    from: 'From',
    to: 'To',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    appointmentOnly: 'By appointment only',
    cancelButton: 'Cancel',
    saveButton: 'Save',
    countryIsRequired: 'Country is required',
    regionIsRequired: 'Region is required',
    classificationIsRequired: 'Classification is required',
    domainIsRequired: 'Domain is required',
    domainIsTooLong: 'Domain is too long (max 64 characters)',
    producerIsTooLong: 'Producer is too long (max 64 characters)',
    cityIsTooLong: 'City is too long (max 64 characters)',
    streetIsTooLong: 'Street is too long (max 64 characters)',
    postalCodeIsTooLong: 'Postal code is too long (max 64 characters)',
    postIsTooLong: 'Post is too long (max 64 characters)',
    phoneIsTooLong: 'Phone is too long (max 64 characters)',
    emailIsInvalid: 'E-mail is invalid',
    emailIsTooLong: 'E-mail is too long (max 64 characters)',
    websiteIsTooLong: 'Website is too long (max 64 characters)',
    websiteIsInvalid: 'Website is invalid',
    plantationIsInvalid: 'Plantation is invalid',
    invalidTime: 'Invalid time (hh:mm)',
    fromToRequired: 'From and To are both required',
    toIsInvalid: 'To is invalid (to >= from)',
    addError: 'Unable to add, try again',
    editError: 'Unable to edit, try again',
    photoError: 'Unable to add photo, try again',
    deleteError: 'Unable to remove photo, try again',
    unknownError: 'Something went wrong, try again',
    addPhotos: 'Drop photos here, or click to select photo to upload.',
    addPhotosInfo: 'Only jpgs and pngs will be accepted.',
    addNextInfo: 'Press Enter to add next value'
};

const styles = (theme: Theme) => ({
    formField: {
        width: 300,
        marginRight: 8
    } as CSSProperties,
    suggestion: {
        display: 'block',
    } as CSSProperties,
    suggestionsList: {
        margin: 0,
        padding: 0,
        listStyleType: 'none',
    } as CSSProperties,
    chip: {
        margin: theme.spacing.unit / 2,
    } as CSSProperties
});

type Styles = WithStyles<'formField' | 'suggestion' | 'suggestionsList' | 'chip'>;
//#endregion

//#region Form configuration
const enum FormFieldNames {
    COUNTRY = 'country',
    REGION = 'region',
    SUBREGION = 'subregion',
    APPELLATION = 'appellation',
    DOMAIN = 'domain',
    CLASSIFICATION = 'classification',
    PRODUCER = 'producer',
    STREET = 'street',
    CITY = 'city',
    POSTAL_CODE = 'postalCode',
    POST = 'post',
    PHONE = 'phone',
    EMAIL = 'email',
    WEBSITE = 'website',
    PLANTATION = 'plantation',
    RED_VARIETIES = 'redVarieties',
    WHITE_VARIETIES = 'whiteVarieties',
    LABEL = 'label',
    VISITING_HOURS_MONDAY_FROM = 'visitingHoursMondayFrom',
    VISITING_HOURS_MONDAY_TO = 'visitingHoursMondayTo',
    VISITING_HOURS_TUESDAY_FROM = 'visitingHoursTuesdayFrom',
    VISITING_HOURS_TUESDAY_TO = 'visitingHoursTuesdayTo',
    VISITING_HOURS_WEDNESDAY_FROM = 'visitingHoursWednesdayFrom',
    VISITING_HOURS_WEDNESDAY_TO = 'visitingHoursWednesdayTo',
    VISITING_HOURS_THURSDAY_FROM = 'visitingHoursThursdayFrom',
    VISITING_HOURS_THURSDAY_TO = 'visitingHoursThursdayTo',
    VISITING_HOURS_FRIDAY_FROM = 'visitingHoursFridayFrom',
    VISITING_HOURS_FRIDAY_TO = 'visitingHoursFridayTo',
    VISITING_HOURS_SATURDAY_FROM = 'visitingHoursSaturdayFrom',
    VISITING_HOURS_SATURDAY_TO = 'visitingHoursSaturdayTo',
    VISITING_HOURS_SUNDAY_FROM = 'visitingHoursSundayFrom',
    VISITING_HOURS_SUNDAY_TO = 'visitingHoursSundayTo',
    APPOINTMENT_ONLY = 'appointmentOnly'
}

interface FormFields {
    country: Request.ObjectData | null;
    region: Request.ObjectData | null;
    subregion: Request.ObjectData | null;
    appellation: Request.ObjectData | null;
    domain: string | null;
    classification: Request.ObjectData | null;
    producer: string | null;
    city: string | null;
    street: string | null;
    postalCode: string | null;
    post: string | null;
    phone: string | null;
    email: string | null;
    website: string | null;
    plantation: string | null;
    redVariety: string | null;
    redVarieties: Request.ObjectData[];
    whiteVariety: string | null;
    whiteVarieties: Request.ObjectData[];
    label: string | null;
    labels: Request.ObjectData[];
    photos: Request.ObjectData[];
    latitude: number | null;
    longitude: number | null;
    visitingHoursMondayFrom: null | string;
    visitingHoursMondayTo: null | string;
    visitingHoursTuesdayFrom: null | string;
    visitingHoursTuesdayTo: null | string;
    visitingHoursWednesdayFrom: null | string;
    visitingHoursWednesdayTo: null | string;
    visitingHoursThursdayFrom: null | string;
    visitingHoursThursdayTo: null | string;
    visitingHoursFridayFrom: null | string;
    visitingHoursFridayTo: null | string;
    visitingHoursSaturdayFrom: null | string;
    visitingHoursSaturdayTo: null | string;
    visitingHoursSundayFrom: null | string;
    visitingHoursSundayTo: null | string;
    appointmentOnly: boolean;
}

interface FormErrors {
    country?: string;
    region?: string;
    domain?: string;
    classification?: string;
    producer?: string;
    city?: string;
    street?: string;
    postalCode?: string;
    post?: string;
    phone?: string;
    email?: string;
    website?: string;
    plantation?: string;
    visitingHoursMondayFrom?: string;
    visitingHoursMondayTo?: string;
    visitingHoursTuesdayFrom?: string;
    visitingHoursTuesdayTo?: string;
    visitingHoursWednesdayFrom?: string;
    visitingHoursWednesdayTo?: string;
    visitingHoursThursdayFrom?: string;
    visitingHoursThursdayTo?: string;
    visitingHoursFridayFrom?: string;
    visitingHoursFridayTo?: string;
    visitingHoursSaturdayFrom?: string;
    visitingHoursSaturdayTo?: string;
    visitingHoursSundayFrom?: string;
    visitingHoursSundayTo?: string;
}
//#endregion

//#region Props & State
interface WineryFormProps {
    item?: Model.Winery;
    onItemSaved: (model: Model.Winery, isNew: boolean) => void
    onCancelItemForm: () => void
}
interface WineryFormState {
    saving: boolean;
    savingPhoto: boolean;
    error: null | string;
    fields: FormFields;
    errors: FormErrors;
    showCountries: boolean;
    loadingCountries: boolean;
    countries: Model.Countries[]

    regions: Model.Appellation[]
    regionsLoading: boolean,
    regionsLoaded: boolean,

    subregions: Model.Appellation[]
    subregionsLoading: boolean,
    subregionsLoaded: boolean,

    appellations: Model.Appellation[]
    appellationsLoading: boolean,
    appellationsLoaded: boolean,

    classifications: Model.Appellation[]
    classificationsLoading: boolean,
    classificationsLoaded: boolean,

    redVarieties: Model.Appellation[]
    redVarietiesLoading: boolean,
    redVarietiesLoaded: boolean,

    whiteVarieties: Model.Appellation[]
    whiteVarietiesLoading: boolean,
    whiteVarietiesLoaded: boolean,

    suggesstions: Array<{ id: number, name: string }>
}

type PropsWithStyles = WineryFormProps & Styles;

const initialState = (item: Model.Winery | undefined): WineryFormState => ({
    saving: false,
    savingPhoto: false,
    error: null,
    fields: {
        country: !!item ? Request.ObjectData.from(item.country) : null,
        region: !!item ? Request.ObjectData.from(item.region) : null,
        subregion: !!item && !!item.subregion ? Request.ObjectData.from(item.subregion) : null,
        appellation: !!item && !!item.appellation ? Request.ObjectData.from(item.appellation) : null,
        domain: !!item ? item.domain : null,
        classification: !!item ? Request.ObjectData.from(item.classification) : null,
        producer: !!item && !!item.producer ? item.producer : null,
        city: !!item && !!item.address ? item.address.city : null,
        street: !!item && !!item.address ? item.address.street : null,
        postalCode: !!item && !!item.address ? item.address.postalCode : null,
        post: !!item && !!item.address ? item.address.post : null,
        phone: !!item ? item.phone : null,
        email: !!item ? item.email : null,
        website: !!item ? item.website : null,
        plantation: !!item && !!item.plantation ? item.plantation.toString(10) : null,
        redVariety: null,
        redVarieties: !!item && item.redVarieties.length > 0 ? item.redVarieties.map(redVariety => Request.ObjectData.from(redVariety)) : [],
        whiteVariety: null,
        whiteVarieties: !!item && item.whiteVarieties.length > 0 ? item.whiteVarieties.map(whiteVariety => Request.ObjectData.from(whiteVariety)) : [],
        label: null,
        labels: !!item && item.labels.length > 0 ? item.labels.map(label => Request.ObjectData.from(label)) : [],
        photos: !!item && item.photos.length > 0 ? item.photos.map(photo => Request.ObjectData.from({ id: photo.id, name: photo.filename })) : [],
        latitude: !!item && !!item.location ? item.location.latitude : null,
        longitude: !!item && !!item.location ? item.location.longitude : null,
        visitingHoursMondayFrom: !!item && !!item.visitingHoursMondayFrom ? item.visitingHoursMondayFrom : null,
        visitingHoursMondayTo: !!item && !!item.visitingHoursMondayTo ? item.visitingHoursMondayTo : null,
        visitingHoursTuesdayFrom: !!item && !!item.visitingHoursTuesdayFrom ? item.visitingHoursTuesdayFrom : null,
        visitingHoursTuesdayTo: !!item && !!item.visitingHoursTuesdayTo ? item.visitingHoursTuesdayTo : null,
        visitingHoursWednesdayFrom: !!item && !!item.visitingHoursWednesdayFrom ? item.visitingHoursWednesdayFrom : null,
        visitingHoursWednesdayTo: !!item && !!item.visitingHoursWednesdayTo ? item.visitingHoursWednesdayTo : null,
        visitingHoursThursdayFrom: !!item && !!item.visitingHoursThursdayFrom ? item.visitingHoursThursdayFrom : null,
        visitingHoursThursdayTo: !!item && !!item.visitingHoursThursdayTo ? item.visitingHoursThursdayTo : null,
        visitingHoursFridayFrom: !!item && !!item.visitingHoursFridayFrom ? item.visitingHoursFridayFrom : null,
        visitingHoursFridayTo: !!item && !!item.visitingHoursFridayTo ? item.visitingHoursFridayTo : null,
        visitingHoursSaturdayFrom: !!item && !!item.visitingHoursSaturdayFrom ? item.visitingHoursSaturdayFrom : null,
        visitingHoursSaturdayTo: !!item && !!item.visitingHoursSaturdayTo ? item.visitingHoursSaturdayTo : null,
        visitingHoursSundayFrom: !!item && !!item.visitingHoursSundayFrom ? item.visitingHoursSundayFrom : null,
        visitingHoursSundayTo: !!item && !!item.visitingHoursSundayTo ? item.visitingHoursSundayTo : null,
        appointmentOnly: !!item ? item.appointmentOnly : false
    },
    errors: {},
    showCountries: false,
    loadingCountries: false,
    countries: [],

    regions: [],
    regionsLoading: false,
    regionsLoaded: false,

    subregions: [],
    subregionsLoading: false,
    subregionsLoaded: false,

    appellations: [],
    appellationsLoading: false,
    appellationsLoaded: false,

    classifications: [],
    classificationsLoading: false,
    classificationsLoaded: false,

    redVarieties: [],
    redVarietiesLoading: false,
    redVarietiesLoaded: false,

    whiteVarieties: [],
    whiteVarietiesLoading: false,
    whiteVarietiesLoaded: false,

    suggesstions: []
});
//#endregion

const ItemsAutosuggest = Autosuggest as new () => Autosuggest<{ id: number, name: string }>;

class WineryForm extends React.Component<PropsWithStyles, WineryFormState> {

    private labelInput: HTMLInputElement;

    private readonly match = require('autosuggest-highlight/match');
    private readonly parse = require('autosuggest-highlight/parse');

    constructor(props: PropsWithStyles) {
        super(props);

        this.state = initialState(props.item);
    }

    render() {
        const { classes, onCancelItemForm } = this.props;
        const { error, errors, fields, showCountries, loadingCountries, countries, suggesstions, saving, savingPhoto,
            regionsLoading, subregionsLoading, appellationsLoading, classificationsLoading } = this.state;
        const { latitude, longitude } = fields;

        const countryItems = [];
        for (const item of countries) {
            countryItems.push({ id: item.continent.id, name: item.continent.name, disabled: true });
            for (const country of item.countries) {
                countryItems.push({ id: country.id, name: country.name, disabled: false });
            }
        }

        const autosuggestTheme: Autosuggest.Theme = {
            suggestionsList: classes.suggestionsList,
            suggestion: classes.suggestion,
        };

        const visitingHours = [
            {
                day: strings.monday, fields: [
                    { value: fields.visitingHoursMondayFrom, label: strings.from, error: errors.visitingHoursMondayFrom, name: FormFieldNames.VISITING_HOURS_MONDAY_FROM },
                    { value: fields.visitingHoursMondayTo, label: strings.to, error: errors.visitingHoursMondayTo, name: FormFieldNames.VISITING_HOURS_MONDAY_TO }
                ]
            },
            {
                day: strings.tuesday, fields: [
                    { value: fields.visitingHoursTuesdayFrom, label: strings.from, error: errors.visitingHoursTuesdayFrom, name: FormFieldNames.VISITING_HOURS_TUESDAY_FROM },
                    { value: fields.visitingHoursTuesdayTo, label: strings.to, error: errors.visitingHoursTuesdayTo, name: FormFieldNames.VISITING_HOURS_TUESDAY_TO }
                ]
            },
            {
                day: strings.wednesday, fields: [
                    { value: fields.visitingHoursWednesdayFrom, label: strings.from, error: errors.visitingHoursWednesdayFrom, name: FormFieldNames.VISITING_HOURS_WEDNESDAY_FROM },
                    { value: fields.visitingHoursWednesdayTo, label: strings.to, error: errors.visitingHoursWednesdayTo, name: FormFieldNames.VISITING_HOURS_WEDNESDAY_TO }
                ]
            },
            {
                day: strings.thursday, fields: [
                    { value: fields.visitingHoursThursdayFrom, label: strings.from, error: errors.visitingHoursThursdayFrom, name: FormFieldNames.VISITING_HOURS_THURSDAY_FROM },
                    { value: fields.visitingHoursThursdayTo, label: strings.to, error: errors.visitingHoursThursdayTo, name: FormFieldNames.VISITING_HOURS_THURSDAY_TO }
                ]
            },
            {
                day: strings.friday, fields: [
                    { value: fields.visitingHoursFridayFrom, label: strings.from, error: errors.visitingHoursFridayFrom, name: FormFieldNames.VISITING_HOURS_FRIDAY_FROM },
                    { value: fields.visitingHoursFridayTo, label: strings.to, error: errors.visitingHoursFridayTo, name: FormFieldNames.VISITING_HOURS_FRIDAY_TO }
                ]
            },
            {
                day: strings.saturday, fields: [
                    { value: fields.visitingHoursSaturdayFrom, label: strings.from, error: errors.visitingHoursSaturdayFrom, name: FormFieldNames.VISITING_HOURS_SATURDAY_FROM },
                    { value: fields.visitingHoursSaturdayTo, label: strings.to, error: errors.visitingHoursSaturdayTo, name: FormFieldNames.VISITING_HOURS_SATURDAY_TO }
                ]
            },
            {
                day: strings.sunday, fields: [
                    { value: fields.visitingHoursSundayFrom, label: strings.from, error: errors.visitingHoursSundayFrom, name: FormFieldNames.VISITING_HOURS_SUNDAY_FROM },
                    { value: fields.visitingHoursSundayTo, label: strings.to, error: errors.visitingHoursSundayTo, name: FormFieldNames.VISITING_HOURS_SUNDAY_TO }
                ]
            }
        ];

        return (
            <React.Fragment>
                <Dialog open={true} fullWidth={true} style={{ zIndex: 999 }}>
                    <DialogTitle>{strings.title}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>{strings.message}</DialogContentText>

                        <FormControl margin="normal" error={!!errors.country} fullWidth={true} required={true}>
                            <InputLabel>{strings.country}</InputLabel>
                            <Select
                                endAdornment={loadingCountries && <InputAdornment position="end"><CircularProgress size={24} /></InputAdornment>}
                                value={fields.country && fields.country.id > 0 ? fields.country.id : []}
                                onChange={this.onCountrySelectChange}
                                displayEmpty={true}
                                onOpen={this.onCountrySelectOpen}
                                onClose={this.onCountrySelectClose}
                                open={showCountries}
                                renderValue={value => fields.country ? fields.country.value : undefined}
                                inputProps={{
                                    name: FormFieldNames.COUNTRY,
                                    id: FormFieldNames.COUNTRY,
                                }}
                            >
                                {countryItems.map((country, index) => (
                                    <MenuItem key={index} value={country.id} divider={country.disabled} disabled={country.disabled}>{country.name}</MenuItem>
                                ))}
                            </Select>
                            {!!errors.country && <FormHelperText>{errors.country}</FormHelperText>}
                        </FormControl>

                        <Grid container={true} spacing={8}>
                            <Grid item={true} xs={6}>
                                <ItemsAutosuggest
                                    theme={autosuggestTheme}
                                    suggestions={suggesstions}
                                    onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
                                    renderSuggestionsContainer={this.renderSuggestionsContainer}
                                    getSuggestionValue={suggesstion => suggesstion.name}
                                    renderSuggestion={this.renderSuggestion}
                                    renderInputComponent={inputProps => this.renderInputComponent(FormFieldNames.REGION, strings.region, true, errors.region, regionsLoading, inputProps)}
                                    onSuggestionsFetchRequested={this.handleRegionSuggestionsFetchRequested}
                                    onSuggestionSelected={(event, data) => {
                                        this.setState({
                                            fields: {
                                                ...this.state.fields,
                                                [FormFieldNames.REGION]: Request.ObjectData.from(data.suggestion),
                                                [FormFieldNames.SUBREGION]: null
                                            },
                                            subregions: [],
                                            subregionsLoaded: false
                                        });
                                    }}
                                    inputProps={{
                                        value: fields.region ? fields.region.value : '',
                                        onChange: (event: React.FormEvent<HTMLInputElement>, params: Autosuggest.ChangeEvent) => {
                                            this.setState({
                                                fields: {
                                                    ...this.state.fields,
                                                    [FormFieldNames.REGION]: Request.ObjectData.empty(params.newValue),
                                                    [FormFieldNames.SUBREGION]: null
                                                },
                                                subregions: [],
                                                subregionsLoaded: false
                                            });
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item={true} xs={6}>
                                <ItemsAutosuggest
                                    theme={autosuggestTheme}
                                    suggestions={suggesstions}
                                    onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
                                    renderSuggestionsContainer={this.renderSuggestionsContainer}
                                    getSuggestionValue={suggesstion => suggesstion.name}
                                    renderSuggestion={this.renderSuggestion}
                                    renderInputComponent={inputProps => this.renderInputComponent(FormFieldNames.SUBREGION, strings.subregion, false, undefined, subregionsLoading, inputProps)}
                                    onSuggestionsFetchRequested={this.handleSubregionSuggestionsFetchRequested}
                                    onSuggestionSelected={(event, data) => { this.setState({ fields: { ...this.state.fields, [FormFieldNames.SUBREGION]: Request.ObjectData.from(data.suggestion) } }); }}
                                    inputProps={{
                                        value: fields.subregion ? fields.subregion.value : '',
                                        onChange: (event: React.FormEvent<HTMLInputElement>, params: Autosuggest.ChangeEvent) => {
                                            this.handleAutosugestChange(FormFieldNames.SUBREGION, params.newValue);
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container={true} spacing={8}>
                            <Grid item={true} xs={6}>
                                <ItemsAutosuggest
                                    theme={autosuggestTheme}
                                    suggestions={suggesstions}
                                    onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
                                    renderSuggestionsContainer={this.renderSuggestionsContainer}
                                    getSuggestionValue={suggesstion => suggesstion.name}
                                    renderSuggestion={this.renderSuggestion}
                                    renderInputComponent={inputProps => this.renderInputComponent(FormFieldNames.APPELLATION, strings.appellation, false, undefined, appellationsLoading, inputProps)}
                                    onSuggestionsFetchRequested={this.handleAppellationSuggestionsFetchRequested}
                                    onSuggestionSelected={(event, data) => { this.setState({ fields: { ...this.state.fields, [FormFieldNames.APPELLATION]: Request.ObjectData.from(data.suggestion) } }); }}
                                    inputProps={{
                                        value: fields.appellation ? fields.appellation.value : '',
                                        onChange: (event: React.FormEvent<HTMLInputElement>, params: Autosuggest.ChangeEvent) => {
                                            this.handleAutosugestChange(FormFieldNames.APPELLATION, params.newValue);
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item={true} xs={6}>
                                <ItemsAutosuggest
                                    theme={autosuggestTheme}
                                    suggestions={suggesstions}
                                    onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
                                    renderSuggestionsContainer={this.renderSuggestionsContainer}
                                    getSuggestionValue={suggesstion => suggesstion.name}
                                    renderSuggestion={this.renderSuggestion}
                                    renderInputComponent={inputProps => this.renderInputComponent(FormFieldNames.CLASSIFICATION, strings.classification, true, errors.classification, classificationsLoading, inputProps)}
                                    onSuggestionsFetchRequested={this.handleClassificationSuggestionsFetchRequested}
                                    onSuggestionSelected={(event, data) => { this.setState({ fields: { ...this.state.fields, [FormFieldNames.CLASSIFICATION]: Request.ObjectData.from(data.suggestion) } }); }}
                                    inputProps={{
                                        value: fields.classification ? fields.classification.value : '',
                                        onChange: (event: React.FormEvent<HTMLInputElement>, params: Autosuggest.ChangeEvent) => {
                                            this.handleAutosugestChange(FormFieldNames.CLASSIFICATION, params.newValue);
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container={true} spacing={8}>
                            <Grid item={true} xs={6}>
                                <TextField
                                    fullWidth={true}
                                    required={true}
                                    margin="normal"
                                    error={!!errors.domain}
                                    label={strings.domain}
                                    helperText={errors.domain}
                                    id={FormFieldNames.DOMAIN}
                                    name={FormFieldNames.DOMAIN}
                                    onChange={this.onFieldChange}
                                    value={fields.domain ? fields.domain : ''}
                                />
                            </Grid>
                            <Grid item={true} xs={6}>
                                <TextField
                                    margin="normal"
                                    error={!!errors.producer}
                                    fullWidth={true}
                                    required={false}
                                    label={strings.producer}
                                    id={FormFieldNames.PRODUCER}
                                    name={FormFieldNames.PRODUCER}
                                    onChange={this.onFieldChange}
                                    helperText={errors.producer}
                                    value={fields.producer ? fields.producer : ''}
                                />
                            </Grid>
                        </Grid>

                        <Grid container={true} spacing={8}>
                            <Grid item={true} xs={6}>
                                <TextField
                                    margin="normal"
                                    error={!!errors.city}
                                    fullWidth={true}
                                    label={strings.city}
                                    id={FormFieldNames.CITY}
                                    name={FormFieldNames.CITY}
                                    onChange={this.onFieldChange}
                                    helperText={errors.city}
                                    value={fields.city ? fields.city : ''}
                                />
                            </Grid>
                            <Grid item={true} xs={6}>
                                <TextField
                                    margin="normal"
                                    error={!!errors.street}
                                    fullWidth={true}
                                    label={strings.street}
                                    id={FormFieldNames.STREET}
                                    name={FormFieldNames.STREET}
                                    onChange={this.onFieldChange}
                                    helperText={errors.street}
                                    value={fields.street ? fields.street : ''}
                                />
                            </Grid>
                        </Grid>

                        <Grid container={true} spacing={8}>
                            <Grid item={true} xs={6}>
                                <TextField
                                    margin="normal"
                                    error={!!errors.postalCode}
                                    fullWidth={true}
                                    label={strings.postalCode}
                                    id={FormFieldNames.POSTAL_CODE}
                                    name={FormFieldNames.POSTAL_CODE}
                                    onChange={this.onFieldChange}
                                    helperText={errors.postalCode}
                                    value={fields.postalCode ? fields.postalCode : ''}
                                />
                            </Grid>
                            <Grid item={true} xs={6}>
                                <TextField
                                    margin="normal"
                                    error={!!errors.post}
                                    fullWidth={true}
                                    label={strings.post}
                                    id={FormFieldNames.POST}
                                    name={FormFieldNames.POST}
                                    onChange={this.onFieldChange}
                                    helperText={errors.post}
                                    value={fields.post ? fields.post : ''}
                                />
                            </Grid>
                        </Grid>

                        <MapComponent
                            latitude={fields.latitude}
                            longitude={fields.longitude}
                            googleMapURL="https://maps.googleapis.com/maps/api/js?v=3.exp&key=AIzaSyB32wO2l289zctjMKaJAiplR6Xc3jxbn3M&libraries=geometry,drawing,places"
                            containerElement={<div style={{ height: `300px` }} />}
                            loadingElement={<div style={{ height: `100%` }} />}
                            mapElement={<div style={{ height: `100%` }} />}
                            onSetLocation={(lat: number, lng: number) => {
                                this.setState({
                                    fields: {
                                        ...fields,
                                        latitude: lat,
                                        longitude: lng
                                    }
                                });
                            }}
                        />

                        {!!latitude && !!longitude && <Button
                            color="primary"
                            variant="raised"
                            style={{ width: '100%' }}
                            onClick={() => {
                                this.setState({
                                    fields: {
                                        ...fields,
                                        latitude: null,
                                        longitude: null
                                    }
                                });
                            }}>
                            {strings.clearLocation}
                        </Button>}

                        <Grid container={true} spacing={8}>
                            <Grid item={true} xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth={true}
                                    label={strings.phone}
                                    id={FormFieldNames.PHONE}
                                    error={!!errors.phone}
                                    helperText={errors.phone}
                                    name={FormFieldNames.PHONE}
                                    onChange={this.onFieldChange}
                                    value={fields.phone ? fields.phone : ''}
                                />
                            </Grid>
                            <Grid item={true} xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth={true}
                                    label={strings.email}
                                    id={FormFieldNames.EMAIL}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    name={FormFieldNames.EMAIL}
                                    onChange={this.onFieldChange}
                                    value={fields.email ? fields.email : ''}
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            margin="normal"
                            fullWidth={true}
                            label={strings.website}
                            id={FormFieldNames.WEBSITE}
                            error={!!errors.website}
                            helperText={errors.website}
                            name={FormFieldNames.WEBSITE}
                            onChange={this.onFieldChange}
                            value={fields.website ? fields.website : ''}
                        />

                        {visitingHours.map((item, index) => (
                            <Grid container={true} alignContent="center" key={index} style={{ marginTop: 16, marginBottom: 8 }}>
                                <Grid item={true}>
                                    <Typography variant="body1">{item.day}</Typography>
                                </Grid>
                                <Grid container={true} spacing={8}>
                                    {item.fields.map((field, fieldIndex) => (
                                        <Grid item={true} xs={6} key={fieldIndex}>
                                            <TextField
                                                fullWidth={true}
                                                margin="none"
                                                error={!!field.error}
                                                label={field.label}
                                                helperText={field.error}
                                                id={field.name}
                                                name={field.name}
                                                onChange={this.onFieldChange}
                                                value={field.value ? field.value : ''}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                <Grid item={true} />
                            </Grid>
                        ))}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={fields.appointmentOnly}
                                    onChange={event => {
                                        this.setState({
                                            fields: { ...this.state.fields, appointmentOnly: event.target.checked }
                                        });
                                    }}
                                    value={FormFieldNames.APPOINTMENT_ONLY}
                                    color="primary"
                                />
                            }
                            label={strings.appointmentOnly}
                        />

                        <TextField
                            margin="normal"
                            fullWidth={true}
                            label={strings.plantation}
                            error={!!errors.plantation}
                            helperText={errors.plantation}
                            id={FormFieldNames.PLANTATION}
                            name={FormFieldNames.PLANTATION}
                            onChange={this.onFieldChange}
                            value={fields.plantation ? fields.plantation : ''}
                        />

                        <Dropzone
                            disabled={savingPhoto}
                            multiple={false}
                            accept="image/jpeg, image/png"
                            style={{
                                width: 'auto',
                                height: 100,
                                borderWidth: 2,
                                borderColor: '#666666',
                                borderStyle: 'dashed',
                                borderRadius: 5,
                                marginTop: 16,
                                position: 'relative'
                            }}
                            acceptStyle={{
                                borderColor: '#66cc66',
                                borderStyle: 'solid'
                            }}
                            rejectStyle={{
                                borderColor: '#cc6666',
                                borderStyle: 'solid'
                            }}
                            onDrop={(accepted, rejected, event) => {
                                if (accepted.length > 0) {
                                    this.setState({ savingPhoto: true });
                                    API.photo.add(accepted[0])
                                        .then(model => {
                                            this.setState({
                                                savingPhoto: false,
                                                fields: {
                                                    ...fields,
                                                    photos: fields.photos.concat(new Request.ObjectData(model.id, model.filename))
                                                }
                                            }
                                            )
                                        })
                                        .catch(() => this.setState({ savingPhoto: false, error: strings.photoError }));
                                }
                            }}
                        >
                            <Grid style={{ height: '100%' }} container={true} justify="center" alignItems="center" alignContent="center">
                                <Typography>{strings.addPhotos}</Typography>
                                <Typography>{strings.addPhotosInfo}</Typography>
                                {savingPhoto && <CircularProgress style={{ position: 'absolute' }} size={48} />}
                            </Grid>
                        </Dropzone>

                        {fields.photos.length > 0 && <Paper style={{ marginTop: 8 }}>
                            {fields.photos.map((photo, index) => {
                                return (
                                    <Chip
                                        style={{
                                            height: 64
                                        }}
                                        key={index}
                                        avatar={
                                            <img
                                                src={`${process.env.REACT_APP_API_URL}photos/${photo.id}/${photo.value}?size=100x100`}
                                                style={{ height: 50, width: 50, marginLeft: 8 }}
                                            />
                                        }
                                        onDelete={() => {
                                            if (savingPhoto) {
                                                return;
                                            }
                                            this.setState({ savingPhoto: true });
                                            API.photo.delete(photo.id, photo.value)
                                                .then(() => {
                                                    this.setState({
                                                        savingPhoto: false,
                                                        fields: {
                                                            ...fields,
                                                            photos: fields.photos.filter((item, i) => index !== i)
                                                        }
                                                    });
                                                })
                                                .catch(() => this.setState({ savingPhoto: false, error: strings.deleteError }));

                                        }}
                                        className={classes.chip}
                                    />
                                );
                            })}
                        </Paper>}

                        <ItemsAutosuggest
                            theme={autosuggestTheme}
                            suggestions={suggesstions}
                            onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
                            renderSuggestionsContainer={this.renderSuggestionsContainer}
                            getSuggestionValue={suggesstion => suggesstion.name}
                            renderSuggestion={this.renderSuggestion}
                            renderInputComponent={inputProps => this.renderRedVarietyInputComponent(inputProps)}
                            onSuggestionsFetchRequested={this.handleRedVarietySuggestionsFetchRequested}
                            onSuggestionSelected={(event, data) => {
                                if (!!data.suggestion) {
                                    if (this.hasRedVariety(data.suggestion.name)) {
                                        this.setState({
                                            fields: {
                                                ...fields,
                                                redVariety: ''
                                            }
                                        });
                                    } else {
                                        event.preventDefault();
                                        this.setState({
                                            fields: {
                                                ...this.state.fields,
                                                redVariety: '',
                                                redVarieties: fields.redVarieties.concat(Request.ObjectData.from(data.suggestion))
                                            }
                                        });
                                    }
                                }
                            }}
                            inputProps={{
                                value: !!fields.redVariety ? fields.redVariety : '',
                                onChange: (event: React.FormEvent<HTMLInputElement>, params: Autosuggest.ChangeEvent) => {
                                    this.setState({
                                        fields: { ...this.state.fields, redVariety: params.newValue }
                                    });
                                }
                            }}
                        />

                        {fields.redVarieties.length > 0 && <Paper>
                            {fields.redVarieties.map((redVariety, index) => {
                                return (
                                    <Chip
                                        key={index}
                                        label={redVariety.value}
                                        onDelete={() => {
                                            this.setState({
                                                fields: {
                                                    ...fields,
                                                    redVarieties: fields.redVarieties.filter((item, i) => index !== i)
                                                }
                                            });
                                        }}
                                        className={classes.chip}
                                    />
                                );
                            })}
                        </Paper>}

                        <ItemsAutosuggest
                            theme={autosuggestTheme}
                            suggestions={suggesstions}
                            onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
                            renderSuggestionsContainer={this.renderSuggestionsContainer}
                            getSuggestionValue={suggesstion => suggesstion.name}
                            renderSuggestion={this.renderSuggestion}
                            renderInputComponent={inputProps => this.renderWhiteVarietyInputComponent(inputProps)}
                            onSuggestionsFetchRequested={this.handleWhiteVarietySuggestionsFetchRequested}
                            onSuggestionSelected={(event, data) => {
                                if (!!data.suggestion) {
                                    if (this.hasWhiteVariety(data.suggestion.name)) {
                                        this.setState({
                                            fields: {
                                                ...fields,
                                                whiteVariety: ''
                                            }
                                        });
                                    } else {
                                        event.preventDefault();
                                        this.setState({
                                            fields: {
                                                ...this.state.fields,
                                                whiteVariety: '',
                                                whiteVarieties: fields.whiteVarieties.concat(Request.ObjectData.from(data.suggestion))
                                            }
                                        });
                                    }
                                }
                            }}
                            inputProps={{
                                value: !!fields.whiteVariety ? fields.whiteVariety : '',
                                onChange: (event: React.FormEvent<HTMLInputElement>, params: Autosuggest.ChangeEvent) => {
                                    this.setState({
                                        fields: { ...this.state.fields, whiteVariety: params.newValue }
                                    });
                                }
                            }}
                        />

                        {fields.whiteVarieties.length > 0 && <Paper>
                            {fields.whiteVarieties.map((whiteVariety, index) => {
                                return (
                                    <Chip
                                        key={index}
                                        label={whiteVariety.value}
                                        onDelete={() => {
                                            this.setState({
                                                fields: {
                                                    ...fields,
                                                    whiteVarieties: fields.whiteVarieties.filter((item, i) => index !== i)
                                                }
                                            });
                                        }}
                                        className={classes.chip}
                                    />
                                );
                            })}
                        </Paper>}

                        <TextField
                            margin="normal"
                            fullWidth={true}
                            label={strings.labels}
                            id={FormFieldNames.LABEL}
                            inputRef={ref => this.labelInput = ref}
                            name={FormFieldNames.LABEL}
                            onChange={this.onFieldChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        onClick={() => {
                                            if (!Validator.isEmpty(this.labelInput.value)) {
                                                this.setState({
                                                    fields: {
                                                        ...fields,
                                                        label: '',
                                                        labels: fields.labels.concat(Request.ObjectData.empty(this.labelInput.value))
                                                    }
                                                });
                                                this.labelInput.focus();
                                            }
                                        }}>
                                        <Tooltip title={strings.addNextInfo}>
                                            <AddIcon />
                                        </Tooltip>
                                    </InputAdornment>)
                            }}
                            onKeyPress={event => {
                                if (event.key === 'Enter' && !Validator.isEmpty(this.labelInput.value)) {
                                    this.setState({
                                        fields: {
                                            ...fields,
                                            label: '',
                                            labels: fields.labels.concat(Request.ObjectData.empty(this.labelInput.value))
                                        }
                                    });
                                }
                            }}
                            value={fields.label ? fields.label : ''}
                        />

                        {fields.labels.length > 0 && <Paper>
                            {fields.labels.map((label, index) => {
                                return (
                                    <Chip
                                        key={index}
                                        label={label.value}
                                        onDelete={() => {
                                            this.setState({
                                                fields: {
                                                    ...fields,
                                                    labels: fields.labels.filter((item, i) => index !== i)
                                                }
                                            });
                                        }}
                                        className={classes.chip}
                                    />
                                );
                            })}
                        </Paper>}
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={onCancelItemForm} color="primary" disabled={saving || savingPhoto}>
                            {strings.cancelButton}
                        </Button>
                        <Button onClick={this.onSaveClick} color="primary" disabled={saving || savingPhoto}>
                            {strings.saveButton}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Portal>
                    <Snackbar
                        style={{
                            zIndex: 1500
                        }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        open={!!error}
                        autoHideDuration={6000}
                        onClose={this.onErrorClose}
                        message={<span>{error}</span>}
                        action={[
                            <IconButton
                                key="close"
                                color="inherit"
                                onClick={this.onErrorClose}
                            >
                                <CloseIcon />
                            </IconButton>
                        ]}
                    />
                </Portal>
            </React.Fragment>
        );
    }

    private handleRegionSuggestionsFetchRequested = (request: Autosuggest.SuggestionsFetchRequestedParams) => {
        const { regions, regionsLoading, regionsLoaded } = this.state;
        const { country } = this.state.fields;

        if (!regionsLoading && regions.length === 0 && !regionsLoaded && country && country.id > 0) {
            this.setState({ regionsLoading: true });
            API.region.list(country.id)
                .then(items => this.setState({ regions: items, regionsLoading: false, regionsLoaded: true }))
                .catch(() => this.setState({ regionsLoading: false }));
        }

        this.setState({ suggesstions: this.getSuggestions(request.value, regions) });
    }

    private handleSubregionSuggestionsFetchRequested = (request: Autosuggest.SuggestionsFetchRequestedParams) => {
        const { subregions, subregionsLoading, subregionsLoaded } = this.state;
        const { region } = this.state.fields;

        if (!subregionsLoading && subregions.length === 0 && !subregionsLoaded && region && region.id > 0) {
            this.setState({ subregionsLoading: true });
            API.subregion.list(region.id)
                .then(items => this.setState({ subregions: items, subregionsLoading: false, subregionsLoaded: true }))
                .catch(() => this.setState({ subregionsLoading: false }));
        }

        this.setState({ suggesstions: this.getSuggestions(request.value, subregions) });
    }

    private handleAppellationSuggestionsFetchRequested = (request: Autosuggest.SuggestionsFetchRequestedParams) => {
        const { appellations, appellationsLoading, appellationsLoaded } = this.state;

        if (!appellationsLoading && appellations.length === 0 && !appellationsLoaded) {
            this.setState({ appellationsLoading: true });
            API.appellation.list()
                .then(items => this.setState({ appellations: items, appellationsLoading: false, appellationsLoaded: true }))
                .catch(() => this.setState({ appellationsLoading: false }));
        }

        this.setState({ suggesstions: this.getSuggestions(request.value, appellations) });
    }

    private handleClassificationSuggestionsFetchRequested = (request: Autosuggest.SuggestionsFetchRequestedParams) => {
        const { classifications, classificationsLoading, classificationsLoaded } = this.state;

        if (!classificationsLoading && classifications.length === 0 && !classificationsLoaded) {
            this.setState({ classificationsLoading: true });
            API.classification.list()
                .then(items => this.setState({ classifications: items, classificationsLoading: false, classificationsLoaded: true }))
                .catch(() => this.setState({ classificationsLoading: false }));
        }

        this.setState({ suggesstions: this.getSuggestions(request.value, classifications) });
    }

    private handleRedVarietySuggestionsFetchRequested = (request: Autosuggest.SuggestionsFetchRequestedParams) => {
        const { redVarieties, redVarietiesLoading, redVarietiesLoaded } = this.state;

        if (!redVarietiesLoading && redVarieties.length === 0 && !redVarietiesLoaded) {
            this.setState({ redVarietiesLoading: true });
            API.redVariety.list()
                .then(items => this.setState({ redVarieties: items, redVarietiesLoading: false, redVarietiesLoaded: true }))
                .catch(() => this.setState({ redVarietiesLoading: false }));
        }

        this.setState({ suggesstions: this.getSuggestions(request.value, redVarieties) });
    }

    private handleWhiteVarietySuggestionsFetchRequested = (request: Autosuggest.SuggestionsFetchRequestedParams) => {
        const { whiteVarieties, whiteVarietiesLoading, whiteVarietiesLoaded } = this.state;

        if (!whiteVarietiesLoading && whiteVarieties.length === 0 && !whiteVarietiesLoaded) {
            this.setState({ whiteVarietiesLoading: true });
            API.whiteVariety.list()
                .then(items => this.setState({ whiteVarieties: items, whiteVarietiesLoading: false, whiteVarietiesLoaded: true }))
                .catch(() => this.setState({ whiteVarietiesLoading: false }));
        }

        this.setState({ suggesstions: this.getSuggestions(request.value, whiteVarieties) });
    }

    private renderSuggestionsContainer = (params: Autosuggest.RenderSuggestionsContainerParams): React.ReactNode => {
        const { containerProps, children } = params;

        return (
            <Paper {...containerProps} square={true}>
                {children}
            </Paper>
        );
    }

    private renderSuggestion = (suggestion: { id: number, name: string }, params: Autosuggest.RenderSuggestionParams) => {
        const matches = this.match(suggestion.name, params.query);
        const parts = this.parse(suggestion.name, matches);

        return (
            <MenuItem selected={params.isHighlighted} component="div">
                <div>
                    {parts.map((part: { text: string; highlight: boolean; }, index: number) => {
                        return part.highlight
                            ? (<span key={String(index)} style={{ fontWeight: 300 }}>{part.text}</span>)
                            : (<strong key={String(index)} style={{ fontWeight: 500 }}>{part.text}</strong>)
                            ;
                    })}
                </div>
            </MenuItem>
        );
    }

    private handleAutosugestChange = (name: string, value: string) => {
        let field: Request.ObjectData | null = null;
        if (!Validator.isEmpty(value)) {
            field = Request.ObjectData.empty(value);
        }
        this.setState({
            fields: { ...this.state.fields, [name]: field }
        });
    }

    private getSuggestions = (value: string, items: Array<{ id: number, name: string }>) => {
        const inputValue = value.trim().toLowerCase();
        const inputLength = inputValue.length;
        let count = 0;

        return inputLength === 0
            ? []
            : items.filter(item => {
                const keep = count < 4 && item.name.toLowerCase().slice(0, inputLength) === inputValue;

                if (keep) {
                    count += 1;
                }

                return keep;
            });
    }

    private handleSuggestionsClearRequested = () => {
        this.setState({
            suggesstions: [],
        });
    }

    private renderInputComponent = (name: string, label: string, required: boolean, error: string | undefined, loading: boolean, inputProps: Autosuggest.InputProps<{ id: number, name: string }>): React.ReactNode => {
        const { ref, value, ...other } = inputProps;

        return (
            <TextField
                margin="normal"
                error={!!error}
                fullWidth={true}
                required={required}
                helperText={error}
                id={name}
                name={name}
                label={label}
                inputRef={ref}
                InputProps={{
                    value,
                    endAdornment: loading ? <InputAdornment position="end"><CircularProgress size={24} /></InputAdornment> : undefined,
                }}
                inputProps={{
                    value,
                    ...other
                }}
            />
        );
    }

    private renderRedVarietyInputComponent = (inputProps: Autosuggest.InputProps<{ id: number, name: string }>): React.ReactNode => {
        const { ref, value, ...other } = inputProps;
        const { fields, redVarietiesLoading } = this.state;

        return (
            <TextField
                margin="normal"
                fullWidth={true}
                label={strings.redVarieties}
                id={FormFieldNames.RED_VARIETIES}
                inputRef={ref}
                name={FormFieldNames.RED_VARIETIES}
                onChange={this.onFieldChange}
                InputProps={{
                    value,
                    endAdornment: redVarietiesLoading ? <InputAdornment position="end"><CircularProgress size={24} /></InputAdornment> : undefined,
                    startAdornment: (
                        <InputAdornment
                            position="start"
                            onClick={() => {
                                const element = document.getElementById(FormFieldNames.RED_VARIETIES) as HTMLInputElement;
                                if (!Validator.isEmpty(element.value)) {
                                    if (this.hasRedVariety(element.value)) {
                                        this.setState({
                                            fields: {
                                                ...fields,
                                                redVariety: ''
                                            }
                                        });
                                    } else {
                                        this.setState({
                                            fields: {
                                                ...fields,
                                                redVariety: '',
                                                redVarieties: fields.redVarieties.concat(Request.ObjectData.empty(element.value))
                                            }
                                        });
                                        element.focus();
                                    }
                                }
                            }}>
                            <Tooltip title={strings.addNextInfo}>
                                <AddIcon />
                            </Tooltip>
                        </InputAdornment>)
                }}
                inputProps={{
                    value,
                    ...other
                }}
                onKeyPress={event => {
                    const target = event.target as HTMLInputElement;
                    if (event.key === 'Enter' && !Validator.isEmpty(target.value)) {
                        if (this.hasRedVariety(target.value)) {
                            this.setState({
                                fields: {
                                    ...fields,
                                    redVariety: ''
                                }
                            });
                        } else {
                            this.setState({
                                fields: {
                                    ...fields,
                                    redVariety: '',
                                    redVarieties: fields.redVarieties.concat(Request.ObjectData.empty(target.value))
                                }
                            });
                        }
                    }
                }}
            />
        );
    }

    private renderWhiteVarietyInputComponent = (inputProps: Autosuggest.InputProps<{ id: number, name: string }>): React.ReactNode => {
        const { ref, value, ...other } = inputProps;
        const { fields, whiteVarietiesLoading } = this.state;

        return (
            <TextField
                margin="normal"
                fullWidth={true}
                label={strings.whiteVarieties}
                id={FormFieldNames.WHITE_VARIETIES}
                inputRef={ref}
                name={FormFieldNames.WHITE_VARIETIES}
                onChange={this.onFieldChange}
                InputProps={{
                    value,
                    endAdornment: whiteVarietiesLoading ? <InputAdornment position="end"><CircularProgress size={24} /></InputAdornment> : undefined,
                    startAdornment: (
                        <InputAdornment
                            position="start"
                            onClick={() => {
                                const element = document.getElementById(FormFieldNames.WHITE_VARIETIES) as HTMLInputElement;
                                if (!Validator.isEmpty(element.value)) {
                                    if (this.hasWhiteVariety(element.value)) {
                                        this.setState({
                                            fields: {
                                                ...fields,
                                                whiteVariety: ''
                                            }
                                        });
                                    } else {
                                        this.setState({
                                            fields: {
                                                ...fields,
                                                whiteVariety: '',
                                                whiteVarieties: fields.whiteVarieties.concat(Request.ObjectData.empty(element.value))
                                            }
                                        });
                                        element.focus();
                                    }
                                }
                            }}>
                            <Tooltip title={strings.addNextInfo}>
                                <AddIcon />
                            </Tooltip>
                        </InputAdornment>)
                }}
                inputProps={{
                    value,
                    ...other
                }}
                onKeyPress={event => {
                    const target = event.target as HTMLInputElement;
                    if (event.key === 'Enter' && !Validator.isEmpty(target.value)) {
                        if (this.hasWhiteVariety(target.value)) {
                            this.setState({
                                fields: {
                                    ...fields,
                                    whiteVariety: ''
                                }
                            });
                        } else {
                            this.setState({
                                fields: {
                                    ...fields,
                                    whiteVariety: '',
                                    whiteVarieties: fields.whiteVarieties.concat(Request.ObjectData.empty(target.value))
                                }
                            });
                        }
                    }
                }}
            />
        );
    }

    private hasRedVariety = (name: string) => {
        const result = this.state.fields.redVarieties.find(redVariety => redVariety.value === name);
        return !!result;
    }

    private hasWhiteVariety = (name: string) => {
        const result = this.state.fields.whiteVarieties.find(whiteVariety => whiteVariety.value === name);
        return !!result;
    }

    private onCountrySelectOpen = () => {
        const { countries, loadingCountries } = this.state;
        if (countries.length === 0 && !loadingCountries) {
            this.setState({ loadingCountries: true });
            API.country.grouped()
                .then(items => {
                    this.setState({ loadingCountries: false, countries: items, showCountries: true });
                })
                .catch(() => {
                    this.setState({ loadingCountries: false });
                });
        } else {
            this.setState({ showCountries: true });
        }
    }

    private onCountrySelectClose = () => {
        this.setState({ showCountries: false });
    }

    private onCountrySelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { countries } = this.state;
        const selectedId = parseInt(event.target.value, 10);

        for (const continent of countries) {
            const selected = _.find(continent.countries, (o) => o.id === selectedId);
            if (selected && (!this.state.fields.country || this.state.fields.country.id !== selected.id)) {
                this.setState({
                    fields: {
                        ...this.state.fields,
                        [event.target.name]: Request.ObjectData.from(selected),
                        [FormFieldNames.REGION]: null,
                        [FormFieldNames.SUBREGION]: null
                    },
                    regions: [],
                    regionsLoaded: false,
                    subregions: [],
                    subregionsLoaded: false
                });
                break;
            } else if (selected) {
                break;
            }
        }
    }

    private onFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            fields: { ...this.state.fields, [event.target.name]: event.target.value }
        });
    }

    private onErrorClose = () => {
        this.setState({ error: null });
    }

    private onSaveClick = () => {
        const errors: FormErrors = this.validateFormFields(this.state.fields);
        this.setState({ errors });

        if (_.isEmpty(errors)) {
            const { item, onItemSaved } = this.props;
            const { fields } = this.state;

            let data: Request.Winery;
            try {
                data = WineryBuilder.builder()
                    .setCountry(!!fields.country ? fields.country.id : null)
                    .setRegion(!!fields.region ? fields.region : null)
                    .setSubregion(!!fields.subregion ? fields.subregion : null)
                    .setAppellation(!!fields.appellation ? fields.appellation : null)
                    .setDomain(!!fields.domain ? fields.domain : null)
                    .setClassification(!!fields.classification ? fields.classification : null)
                    .setProducer(!!fields.producer ? fields.producer : null)
                    .setAddressStreet(!!fields.street ? fields.street : null)
                    .setAddressCity(!!fields.city ? fields.city : null)
                    .setAddressPostalCode(!!fields.postalCode ? fields.postalCode : null)
                    .setAddressPost(!!fields.post ? fields.post : null)
                    .setLatitude(!!fields.latitude ? fields.latitude : null)
                    .setLongitude(!!fields.longitude ? fields.longitude : null)
                    .setPhone(!!fields.phone ? fields.phone : null)
                    .setEmail(!!fields.email ? fields.email : null)
                    .setWebsite(!!fields.website ? fields.website : null)
                    .setVisitingHoursMondayFrom(!!fields.visitingHoursMondayFrom ? fields.visitingHoursMondayFrom : null)
                    .setVisitingHoursMondayTo(!!fields.visitingHoursMondayTo ? fields.visitingHoursMondayTo : null)
                    .setVisitingHoursTuesdayFrom(!!fields.visitingHoursTuesdayFrom ? fields.visitingHoursTuesdayFrom : null)
                    .setVisitingHoursTuesdayTo(!!fields.visitingHoursTuesdayTo ? fields.visitingHoursTuesdayTo : null)
                    .setVisitingHoursWednesdayFrom(!!fields.visitingHoursWednesdayFrom ? fields.visitingHoursWednesdayFrom : null)
                    .setVisitingHoursWednesdayTo(!!fields.visitingHoursWednesdayTo ? fields.visitingHoursWednesdayTo : null)
                    .setVisitingHoursThursdayFrom(!!fields.visitingHoursThursdayFrom ? fields.visitingHoursThursdayFrom : null)
                    .setVisitingHoursThursdayTo(!!fields.visitingHoursThursdayTo ? fields.visitingHoursThursdayTo : null)
                    .setVisitingHoursFridayFrom(!!fields.visitingHoursFridayFrom ? fields.visitingHoursFridayFrom : null)
                    .setVisitingHoursFridayTo(!!fields.visitingHoursFridayTo ? fields.visitingHoursFridayTo : null)
                    .setVisitingHoursSaturdayFrom(!!fields.visitingHoursSaturdayFrom ? fields.visitingHoursSaturdayFrom : null)
                    .setVisitingHoursSaturdayTo(!!fields.visitingHoursSaturdayTo ? fields.visitingHoursSaturdayTo : null)
                    .setVisitingHoursSundayFrom(!!fields.visitingHoursSundayFrom ? fields.visitingHoursSundayFrom : null)
                    .setVisitingHoursSundayTo(!!fields.visitingHoursSundayTo ? fields.visitingHoursSundayTo : null)
                    .setAppointmentOnly(fields.appointmentOnly)
                    .setPlantation(!!fields.plantation ? parseFloat(fields.plantation) : null)
                    .setRedVarieties(fields.redVarieties)
                    .setWhiteVarieties(fields.whiteVarieties)
                    .setLabels(fields.labels)
                    .setPhotos(fields.photos.map(photo => photo.id))
                    .build();
            } catch (e) {
                this.setState({ error: strings.unknownError });
                return;
            }

            this.setState({ saving: true });

            if (!!item && item.id > 0) {
                this.setState({ saving: false, error: strings.editError });
            } else {
                API.winery.add(data)
                    .then(model => {
                        onItemSaved(model, true);
                        this.setState({ saving: false });
                    })
                    .catch(() => {
                        this.setState({ saving: false, error: strings.addError });
                    });
            }
        }
    }

    private validateFormFields(fields: FormFields): FormErrors {
        const errors: FormErrors = {};

        if (!fields.country || fields.country.id <= 0) {
            errors.country = strings.countryIsRequired;
        }

        if (!fields.region) {
            errors.region = strings.regionIsRequired;
        }

        if (!fields.classification) {
            errors.classification = strings.classificationIsRequired;
        }

        if (!fields.domain || Validator.isEmpty(fields.domain)) {
            errors.domain = strings.domainIsRequired;
        } else if (!Validator.isLength(fields.domain, { max: 64 })) {
            errors.domain = strings.domainIsTooLong;
        }

        if (!!fields.producer && !Validator.isLength(fields.producer, { max: 64 })) {
            errors.producer = strings.producerIsTooLong;
        }

        if (!!fields.city && !Validator.isLength(fields.city, { max: 64 })) {
            errors.city = strings.cityIsTooLong;
        }

        if (!!fields.street && !Validator.isLength(fields.street, { max: 64 })) {
            errors.street = strings.streetIsTooLong;
        }

        if (!!fields.postalCode && !Validator.isLength(fields.postalCode, { max: 64 })) {
            errors.postalCode = strings.postalCodeIsTooLong;
        }

        if (!!fields.post && !Validator.isLength(fields.post, { max: 64 })) {
            errors.post = strings.postIsTooLong;
        }

        if (!!fields.latitude && !!fields.longitude && !Validator.isLatLong(`${fields.latitude},${fields.longitude}`)) {
            // TODO:
        }

        if (!!fields.phone && !Validator.isLength(fields.phone, { max: 64 })) {
            errors.phone = strings.phoneIsTooLong;
        }

        if (!!fields.email && !Validator.isEmail(fields.email)) {
            errors.email = strings.emailIsInvalid;
        } else if (!!fields.email && !Validator.isLength(fields.email, { max: 64 })) {
            errors.email = strings.emailIsTooLong;
        }

        if (!!fields.website && !Validator.isURL(fields.website, { require_host: true, require_protocol: true })) {
            errors.website = strings.websiteIsInvalid;
        } else if (!!fields.website && !Validator.isLength(fields.website, { max: 64 })) {
            errors.website = strings.websiteIsTooLong;
        }

        [
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
            'Sunday'
        ].forEach(day => {
            let validTime = true;
            if (!!fields[`visitingHours${day}From`] && !Validator.matches(fields[`visitingHours${day}From`] as string, /^([01]\d|2[0-3]):?([0-5]\d)$/)) {
                errors[`visitingHours${day}From`] = strings.invalidTime;
                validTime = false;
            }
            if (!!fields[`visitingHours${day}To`] && !Validator.matches(fields[`visitingHours${day}To`] as string, /^([01]\d|2[0-3]):?([0-5]\d)$/)) {
                errors[`visitingHours${day}To`] = strings.invalidTime;
                validTime = false;
            }
            if (!!fields[`visitingHours${day}From`] && !fields[`visitingHours${day}To`]) {
                errors[`visitingHours${day}To`] = strings.fromToRequired;
            }
            if (!!fields[`visitingHours${day}To`] && !fields[`visitingHours${day}From`]) {
                errors[`visitingHours${day}From`] = strings.fromToRequired;
            }

            if (!!fields[`visitingHours${day}From`] && !!fields[`visitingHours${day}To`] && validTime) {
                const from = parseInt((fields[`visitingHours${day}From`] as string).replace(':', '0'), 10);
                const to = parseInt((fields[`visitingHours${day}To`] as string).replace(':', '0'), 10);
                if (to < from) {
                    errors[`visitingHours${day}To`] = strings.toIsInvalid;
                }
            }
        });

        if (!!fields.plantation && !Validator.isFloat(fields.plantation)) {
            errors.plantation = strings.plantationIsInvalid;
        }

        return errors;
    }
}

export default withStyles(styles)<WineryFormProps>(WineryForm);



// WEBPACK FOOTER //
// ./src/modules/winery/WineryForm.tsx